// Account-level crypto: password derivation, identity & prekey generation, the
// encrypted key bundle, and safety numbers. None of the password, the wrap key,
// or any private key in here ever leaves the device unencrypted.
import { toB64, fromB64, utf8, fromUtf8 } from "@shared/encoding.js";
import { CTX, KEY_BYTES, SALT_BYTES } from "@shared/constants.js";
import type { RegisterReq } from "@shared/wire.js";
import {
  type KeyPair,
  randomBytes,
  argon2id,
  kdf,
  ed25519FromSeed,
  genX25519,
  genEd25519,
  sign,
  hash,
  aeadSeal,
  aeadOpen,
  wipe,
} from "./sodium.js";

export interface Identity {
  ik_dh: KeyPair; // X25519, for X3DH
  ik_sig: KeyPair; // Ed25519, for signatures + safety number
}

export interface SignedPrekey {
  id: number;
  keypair: KeyPair; // X25519
  sig: Uint8Array; // Ed25519 over keypair.pub by ik_sig
}

export interface OneTimePrekey {
  id: number;
  keypair: KeyPair; // X25519
}

/** Everything derived from the password. None of these ever leaves the device. */
export interface PasswordKeys {
  wrapKey: Uint8Array; // encrypts the server-stored key bundle
  storeKey: Uint8Array; // encrypts the local IndexedDB store at rest
  authKeypair: KeyPair; // Ed25519 used for login challenge-response
}

export function deriveFromPassword(password: string, salt: Uint8Array): PasswordKeys {
  const stretched = argon2id(password, salt, KEY_BYTES);
  const wrapKey = kdf(stretched, CTX.wrap);
  const storeKey = kdf(stretched, CTX.store);
  const authSeed = kdf(stretched, CTX.auth);
  const authKeypair = ed25519FromSeed(authSeed);
  wipe(stretched);
  wipe(authSeed);
  return { wrapKey, storeKey, authKeypair };
}

export const genIdentity = (): Identity => ({ ik_dh: genX25519(), ik_sig: genEd25519() });

function rnd32(): number {
  // Non-secret 32-bit id for prekeys (collision-tolerant; server enforces uniqueness).
  const b = randomBytes(4);
  return ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
}

export function genSignedPrekey(ik_sig: KeyPair): SignedPrekey {
  const keypair = genX25519();
  return { id: rnd32(), keypair, sig: sign(keypair.pub, ik_sig.priv) };
}

export const genOneTimePrekeys = (count: number): OneTimePrekey[] =>
  Array.from({ length: count }, () => ({ id: rnd32(), keypair: genX25519() }));

// ---- Encrypted key bundle --------------------------------------------------

interface BundlePlain {
  v: number;
  ik_dh_priv: string;
  ik_dh_pub: string;
  ik_sig_priv: string;
  ik_sig_pub: string;
  spk: { id: number; priv: string; pub: string; sig: string };
  opks: { id: number; priv: string; pub: string }[];
}

interface BundleEnvelope {
  v: number;
  nonce: string;
  ct: string;
}

export function wrapKeyBundle(
  wrapKey: Uint8Array,
  identity: Identity,
  spk: SignedPrekey,
  opks: OneTimePrekey[],
): string {
  const plain: BundlePlain = {
    v: 1,
    ik_dh_priv: toB64(identity.ik_dh.priv),
    ik_dh_pub: toB64(identity.ik_dh.pub),
    ik_sig_priv: toB64(identity.ik_sig.priv),
    ik_sig_pub: toB64(identity.ik_sig.pub),
    spk: { id: spk.id, priv: toB64(spk.keypair.priv), pub: toB64(spk.keypair.pub), sig: toB64(spk.sig) },
    opks: opks.map((o) => ({ id: o.id, priv: toB64(o.keypair.priv), pub: toB64(o.keypair.pub) })),
  };
  const { nonce, ct } = aeadSeal(wrapKey, utf8(JSON.stringify(plain)));
  const env: BundleEnvelope = { v: 1, nonce: toB64(nonce), ct: toB64(ct) };
  return toB64(utf8(JSON.stringify(env)));
}

export interface UnwrappedBundle {
  identity: Identity;
  spk: SignedPrekey;
  opks: OneTimePrekey[];
}

export function unwrapKeyBundle(wrapKey: Uint8Array, bundleB64: string): UnwrappedBundle {
  const env = JSON.parse(fromUtf8(fromB64(bundleB64))) as BundleEnvelope;
  const plainBytes = aeadOpen(wrapKey, fromB64(env.nonce), fromB64(env.ct)); // throws on wrong password
  const p = JSON.parse(fromUtf8(plainBytes)) as BundlePlain;
  return {
    identity: {
      ik_dh: { pub: fromB64(p.ik_dh_pub), priv: fromB64(p.ik_dh_priv) },
      ik_sig: { pub: fromB64(p.ik_sig_pub), priv: fromB64(p.ik_sig_priv) },
    },
    spk: {
      id: p.spk.id,
      keypair: { pub: fromB64(p.spk.pub), priv: fromB64(p.spk.priv) },
      sig: fromB64(p.spk.sig),
    },
    opks: p.opks.map((o) => ({ id: o.id, keypair: { pub: fromB64(o.pub), priv: fromB64(o.priv) } })),
  };
}

// ---- Registration material -------------------------------------------------

export interface AccountSecrets {
  identity: Identity;
  spk: SignedPrekey;
  opks: OneTimePrekey[];
  wrapKey: Uint8Array;
  storeKey: Uint8Array;
  authKeypair: KeyPair;
}

export interface NewAccount {
  request: RegisterReq;
  secrets: AccountSecrets;
}

/** Build everything needed to register `handle` with `password`, plus the local secrets. */
export function createAccount(handle: string, password: string, opkCount = 100): NewAccount {
  const salt = randomBytes(SALT_BYTES);
  const { wrapKey, storeKey, authKeypair } = deriveFromPassword(password, salt);
  const identity = genIdentity();
  const spk = genSignedPrekey(identity.ik_sig);
  const opks = genOneTimePrekeys(opkCount);
  const key_bundle = wrapKeyBundle(wrapKey, identity, spk, opks);

  const request: RegisterReq = {
    handle,
    salt: toB64(salt),
    auth_pub: toB64(authKeypair.pub),
    ik_dh_pub: toB64(identity.ik_dh.pub),
    ik_sig_pub: toB64(identity.ik_sig.pub),
    signed_prekey: { spk_id: spk.id, spk_pub: toB64(spk.keypair.pub), spk_sig: toB64(spk.sig) },
    one_time_prekeys: opks.map((o) => ({ opk_id: o.id, opk_pub: toB64(o.keypair.pub) })),
    key_bundle,
  };
  return { request, secrets: { identity, spk, opks, wrapKey, storeKey, authKeypair } };
}

// ---- Safety number (contact verification) ----------------------------------

/**
 * A symmetric fingerprint of both parties' identity-signing keys. If two users
 * see the same number, no key was swapped in transit. Rendered as digit groups
 * so a beginner can read it aloud.
 */
export function safetyNumber(ikSigPubA: Uint8Array, ikSigPubB: Uint8Array): string {
  const [lo, hi] = compareBytes(ikSigPubA, ikSigPubB) <= 0 ? [ikSigPubA, ikSigPubB] : [ikSigPubB, ikSigPubA];
  const digest = kdf(hash(concat(lo, hi)), CTX.safety, 30);
  const groups: string[] = [];
  for (let i = 0; i < 30; i += 5) {
    let n = 0;
    for (let j = 0; j < 5; j++) n = (n * 256 + digest[i + j]) % 100000;
    groups.push(n.toString().padStart(5, "0"));
  }
  return groups.join(" ");
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
